import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaRedo } from 'react-icons/fa';
import { toast } from 'react-toastify';
import CommonService from '../../services/common.service';
import {
  dispatchInfrastructureNotificationsRefresh,
  signBuyerInfrastructureListingAgreement,
  signInfrastructureListingAgreement,
} from '../../services/infrastructureLeasingListing.service';
import '../../assets/styles/seller_sign_agreement.scss';

const SIGN_NAME_MAX = 200;
const SIGN_ROLE_MAX = 120;
const SIGNATURE_DATA_MIN_LEN = 80;
const UPLOAD_MAX_BYTES = 2.5 * 1024 * 1024;

function textSignatureToDataUrl(text) {
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 200;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#111827';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = 'italic 42px "Segoe Script", "Brush Script MT", "Apple Chancery", cursive';
  const t = String(text || '').trim() || 'Signature';
  ctx.fillText(t, canvas.width / 2, canvas.height / 2, canvas.width - 40);
  return canvas.toDataURL('image/png');
}

function getCanvasPos(e, canvas) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  const clientX = e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX;
  const clientY = e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY;
  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top) * scaleY,
  };
}

function validateSignForm({ fullName, designation, sigMethod, canvasDirty, canvasRef, typeText, uploadDataUrl }) {
  const errors = {};
  const fn = String(fullName ?? '').trim();
  const des = String(designation ?? '').trim();

  if (!fn) errors.fullName = 'Full name is required.';
  else if (fn.length > SIGN_NAME_MAX) errors.fullName = `Use at most ${SIGN_NAME_MAX} characters.`;

  if (!des) errors.designation = 'Designation is required.';
  else if (des.length > SIGN_ROLE_MAX) errors.designation = `Use at most ${SIGN_ROLE_MAX} characters.`;

  let signatureData = '';
  if (sigMethod === 'draw') {
    const canvas = canvasRef?.current;
    if (!canvas || !canvasDirty) {
      errors.signature = 'Draw your signature on the canvas.';
    } else {
      signatureData = canvas.toDataURL('image/png');
    }
  } else if (sigMethod === 'type') {
    const t = String(typeText ?? '').trim();
    if (!t) errors.signature = 'Type your name as your signature.';
    else signatureData = textSignatureToDataUrl(t);
  } else if (sigMethod === 'upload') {
    const u = String(uploadDataUrl ?? '').trim();
    if (!u) errors.signature = 'Choose an image file for your signature.';
    else if (!/^data:image\//i.test(u)) errors.signature = 'File must be an image (PNG, JPG, GIF, or WebP).';
    else signatureData = u;
  } else {
    errors.signature = 'Select Draw, Type, or Upload.';
  }

  if (!errors.signature && signatureData.length < SIGNATURE_DATA_MIN_LEN) {
    errors.signature = 'Signature data is invalid or incomplete. Try again.';
  }

  const ok = Object.keys(errors).length === 0;
  return {
    ok,
    errors,
    signatureData: ok ? signatureData : null,
    fullName: fn,
    designation: des,
  };
}

function dataURLToBlob(dataUrl) {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new Blob([u8arr], { type: mime });
}

/**
 * CM-style seller sign block: full name, designation, draw/type/upload, acknowledgement, then upload + POST sign.
 */
export default function InfrastructureLeasingAgreementSignPanel({
  listingIdNum,
  enquiryIdNum,
  uploadCompanySlug,
  defaultSignerName = '',
  onSigned,
  /** `'seller'` — listing owner signs first; `'buyer'` — enquirer signs after seller. */
  signRole = 'seller',
}) {
  const [fullName, setFullName] = useState('');
  const [designation, setDesignation] = useState('');
  const [sigMethod, setSigMethod] = useState('draw');
  const [typeText, setTypeText] = useState('');
  const [uploadDataUrl, setUploadDataUrl] = useState('');
  const [canvasDirty, setCanvasDirty] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [signFieldErrors, setSignFieldErrors] = useState({});
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canvasRef = useRef(null);

  useEffect(() => {
    const n = String(defaultSignerName || '').trim();
    if (!n) return;
    setFullName((prev) => (String(prev || '').trim() ? prev : n));
  }, [defaultSignerName]);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const w = 800;
    const h = 240;
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    const ctx2 = canvas.getContext('2d');
    ctx2.setTransform(1, 0, 0, 1, 0, 0);
    ctx2.globalCompositeOperation = 'source-over';
    ctx2.fillStyle = '#ffffff';
    ctx2.fillRect(0, 0, w, h);
    ctx2.strokeStyle = '#111827';
    ctx2.lineWidth = 2.2;
    ctx2.lineCap = 'round';
    ctx2.lineJoin = 'round';
    ctx2.beginPath();
    setDrawing(false);
    setCanvasDirty(false);
  }, []);

  useEffect(() => {
    if (sigMethod === 'draw') {
      initCanvas();
    }
  }, [sigMethod, initCanvas]);

  useEffect(() => {
    setSignFieldErrors((p) => {
      if (!p.signature) return p;
      const n = { ...p };
      delete n.signature;
      return n;
    });
  }, [sigMethod]);

  const onCanvasDown = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    setDrawing(true);
    const pos = getCanvasPos(e, canvas);
    const ctx2 = canvas.getContext('2d');
    ctx2.beginPath();
    ctx2.moveTo(pos.x, pos.y);
  };

  const onCanvasMove = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pos = getCanvasPos(e, canvas);
    const ctx2 = canvas.getContext('2d');
    ctx2.lineTo(pos.x, pos.y);
    ctx2.stroke();
    setCanvasDirty(true);
    setSignFieldErrors((p) => {
      if (!p.signature) return p;
      const n = { ...p };
      delete n.signature;
      return n;
    });
  };

  const onCanvasUp = () => {
    setDrawing(false);
  };

  function onUploadFile(ev) {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      setSignFieldErrors((prev) => ({ ...prev, signature: 'Please choose an image file (PNG, JPG, etc.).' }));
      toast.error('Please choose an image file.');
      ev.target.value = '';
      return;
    }
    if (file.size > UPLOAD_MAX_BYTES) {
      setSignFieldErrors((prev) => ({
        ...prev,
        signature: `Image must be ${Math.round(UPLOAD_MAX_BYTES / (1024 * 1024))} MB or smaller.`,
      }));
      toast.error('File is too large.');
      ev.target.value = '';
      return;
    }
    setSignFieldErrors((prev) => {
      const next = { ...prev };
      delete next.signature;
      return next;
    });
    const reader = new FileReader();
    reader.onerror = () => {
      toast.error('Could not read the file.');
      setSignFieldErrors((prev) => ({ ...prev, signature: 'Could not read the file.' }));
    };
    reader.onload = () => {
      if (typeof reader.result === 'string') setUploadDataUrl(reader.result);
    };
    reader.readAsDataURL(file);
  }

  const handleSign = async () => {
    if (!ack) {
      toast.warning('Please confirm that you agree to sign this agreement.');
      return;
    }
    const validation = validateSignForm({
      fullName,
      designation,
      sigMethod,
      canvasDirty,
      canvasRef,
      typeText,
      uploadDataUrl,
    });
    if (!validation.ok) {
      setSignFieldErrors(validation.errors);
      const first =
        validation.errors.fullName ||
        validation.errors.designation ||
        validation.errors.signature ||
        'Please fix the highlighted fields.';
      toast.error(first);
      return;
    }
    setSignFieldErrors({});
    setSubmitting(true);
    try {
      const blob = dataURLToBlob(validation.signatureData);
      const file = new File([blob], 'signature.png', { type: blob.type || 'image/png' });
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'company');
      fd.append('companyName', uploadCompanySlug);
      fd.append(
        'fileType',
        signRole === 'buyer' ? 'infrastructure_agreement_buyer_signature' : 'infrastructure_agreement_signature'
      );
      const up = await CommonService.post('/upload/single', fd);
      const uploaded = up?.data ?? {};
      const accessPath = uploaded.accessPath ?? uploaded.access_path;
      if (!up?.success || !accessPath) {
        throw new Error(up?.message || 'Signature upload failed');
      }
      const payload = {
        signerName: validation.fullName,
        signerDesignation: validation.designation,
        signatureMethod: sigMethod,
        signatureImagePath: accessPath,
      };
      const res =
        signRole === 'buyer'
          ? await signBuyerInfrastructureListingAgreement(listingIdNum, enquiryIdNum, payload)
          : await signInfrastructureListingAgreement(listingIdNum, enquiryIdNum, payload);
      if (res?.success && res.data?.enquiry) {
        toast.success(res.message || 'Agreement signed.');
        dispatchInfrastructureNotificationsRefresh();
        if (typeof onSigned === 'function') onSigned(res.data);
      } else {
        toast.error(res?.message || 'Could not sign.');
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message;
      toast.error(msg || 'Could not sign agreement.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cm-sign-form">
      <h3 className="mb-3">Sign agreement</h3>
      <p className="text-muted small mb-3">
        {signRole === 'buyer'
          ? 'As the buyer, provide your full name and designation, then sign using Draw, Type, or Upload — after the seller has signed the formal agreement.'
          : 'Provide your full name and designation, then create your signature using Draw, Type, or Upload — same as other digital agreements on the platform.'}
      </p>

      <div className="cm-sign-fields">
        <div>
          <label htmlFor="ile-sign-full-name">Full name</label>
          <input
            id="ile-sign-full-name"
            type="text"
            autoComplete="name"
            className={signFieldErrors.fullName ? 'is-invalid' : ''}
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value);
              setSignFieldErrors((p) => {
                if (!p.fullName) return p;
                const n = { ...p };
                delete n.fullName;
                return n;
              });
            }}
          />
          {signFieldErrors.fullName ? (
            <div className="invalid-feedback d-block">{signFieldErrors.fullName}</div>
          ) : null}
        </div>
        <div>
          <label htmlFor="ile-sign-designation">Designation</label>
          <input
            id="ile-sign-designation"
            type="text"
            autoComplete="organization-title"
            className={signFieldErrors.designation ? 'is-invalid' : ''}
            value={designation}
            onChange={(e) => {
              setDesignation(e.target.value);
              setSignFieldErrors((p) => {
                if (!p.designation) return p;
                const n = { ...p };
                delete n.designation;
                return n;
              });
            }}
          />
          {signFieldErrors.designation ? (
            <div className="invalid-feedback d-block">{signFieldErrors.designation}</div>
          ) : null}
        </div>
      </div>

      <div className="cm-sign-method-tabs">
        {['draw', 'type', 'upload'].map((m) => (
          <button
            key={m}
            type="button"
            className={sigMethod === m ? 'cm-sign-method--active' : ''}
            onClick={() => setSigMethod(m)}
          >
            {m === 'draw' ? 'Draw' : m === 'type' ? 'Type' : 'Upload'}
          </button>
        ))}
      </div>

      {signFieldErrors.signature ? (
        <div className="alert alert-danger py-2 small mb-2" role="alert">
          {signFieldErrors.signature}
        </div>
      ) : null}

      {sigMethod === 'draw' && (
        <>
          <div className="cm-sign-canvas-box">
            {!canvasDirty ? <span className="cm-sign-canvas-hint">Draw your signature below</span> : null}
            <canvas
              ref={canvasRef}
              onMouseDown={onCanvasDown}
              onMouseMove={onCanvasMove}
              onMouseUp={onCanvasUp}
              onMouseLeave={onCanvasUp}
              onTouchStart={onCanvasDown}
              onTouchMove={onCanvasMove}
              onTouchEnd={onCanvasUp}
            />
          </div>
          <div className="cm-sign-canvas-toolbar">
            <button type="button" className="cm-sign-clear" onClick={() => initCanvas()}>
              <FaRedo aria-hidden /> Clear
            </button>
          </div>
        </>
      )}

      {sigMethod === 'type' && (
        <input
          className={`cm-sign-type-input${signFieldErrors.signature ? ' is-invalid' : ''}`}
          placeholder="Type your name"
          value={typeText}
          onChange={(e) => {
            setTypeText(e.target.value);
            setSignFieldErrors((p) => {
              if (!p.signature) return p;
              const n = { ...p };
              delete n.signature;
              return n;
            });
          }}
          aria-invalid={signFieldErrors.signature ? 'true' : undefined}
        />
      )}

      {sigMethod === 'upload' && (
        <div className="cm-sign-upload">
          <div>Upload an image of your signature (PNG or JPG).</div>
          <input type="file" accept="image/*" onChange={onUploadFile} />
          {uploadDataUrl ? (
            <div style={{ marginTop: 12 }}>
              <img src={uploadDataUrl} alt="Upload preview" style={{ maxHeight: 100, borderRadius: 8 }} />
            </div>
          ) : null}
        </div>
      )}

      <div className="form-check w-100 my-3">
        <input
          className="form-check-input"
          type="checkbox"
          id={signRole === 'buyer' ? 'ile-buyer-agree-sign-ack' : 'ile-agree-sign-ack'}
          checked={ack}
          onChange={(e) => setAck(e.target.checked)}
        />
        <label
          className="form-check-label"
          htmlFor={signRole === 'buyer' ? 'ile-buyer-agree-sign-ack' : 'ile-agree-sign-ack'}
        >
          I confirm that I have reviewed this agreement and accept it on behalf of my business. This action records my
          electronic acceptance.
        </label>
      </div>

      <button type="button" className="btn btn-dark" disabled={submitting || !ack} onClick={handleSign}>
        {submitting ? 'Signing…' : 'Sign agreement'}
      </button>
    </div>
  );
}
